<template>
  <el-container class="admin-layout">
    <el-aside width="240px" class="admin-sidebar">
      <!-- 品牌区 -->
      <div class="admin-brand">
        <div class="brand-icon">
          <svg class="w-5 h-5 text-white" fill="none" stroke="currentColor" stroke-width="2" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" d="M13 10V3L4 14h7v7l9-11h-7z" />
          </svg>
        </div>
        <div class="brand-text">
          <div class="brand-title">AI管理能力提升助手</div>
          <div class="brand-subtitle">管理后台 <span class="brand-version">v4.0</span></div>
        </div>
      </div>

      <el-menu
        :default-active="$route.path"
        router
        background-color="transparent"
        text-color="#64748B"
        active-text-color="#FFFFFF"
        class="admin-menu"
      >
        <!-- 数据概览 -->
        <el-menu-item index="/admin">
          <i class="el-icon-s-data" />
          <span slot="title">数据看板</span>
        </el-menu-item>

        <!-- 内容中心 -->
        <el-submenu
          v-if="showGroupContent"
          index="content"
        >
          <template slot="title">
            <i class="el-icon-chat-dot-round" />
            <span>问一问（内容中心）</span>
          </template>
          <el-menu-item
            v-if="hasPerm(PERMISSIONS.VIEW_TOPIC)"
            index="/admin/topics"
          >
            <i class="el-icon-notebook-2" />
            <span slot="title">话题管理</span>
          </el-menu-item>
          <el-menu-item
            v-if="hasPerm(PERMISSIONS.VIEW_CASE)"
            index="/admin/cases"
          >
            <i class="el-icon-collection" />
            <span slot="title">案例管理</span>
          </el-menu-item>
          <el-menu-item
            v-if="hasPerm(PERMISSIONS.VIEW_EXPERT)"
            index="/admin/experts"
          >
            <i class="el-icon-user-solid" />
            <span slot="title">专家管理</span>
          </el-menu-item>
        </el-submenu>

        <!-- 演练中心 -->
        <el-submenu
          v-if="hasPerm(PERMISSIONS.VIEW_SCENARIO)"
          index="practice"
        >
          <template slot="title">
            <i class="el-icon-aim" />
            <span>练一练（演练中心）</span>
          </template>
          <el-menu-item index="/admin/scenarios">
            <i class="el-icon-question" />
            <span slot="title">场景题库</span>
          </el-menu-item>
        </el-submenu>

        <!-- 诊断中心 -->
        <el-submenu
          v-if="hasPerm(PERMISSIONS.VIEW_DIAGNOSIS)"
          index="diagnose"
        >
          <template slot="title">
            <i class="el-icon-cpu" />
            <span>聊一聊（诊断中心）</span>
          </template>
          <el-menu-item index="/admin/diagnosis-templates">
            <i class="el-icon-first-aid-kit" />
            <span slot="title">诊断模板与配置</span>
          </el-menu-item>
        </el-submenu>

        <!-- 用户中心 -->
        <el-submenu
          v-if="showGroupUser"
          index="user"
        >
          <template slot="title">
            <i class="el-icon-s-custom" />
            <span>用户中心</span>
          </template>
          <el-menu-item index="/admin/contributions">
            <i class="el-icon-s-check" />
            <span slot="title">投稿审核</span>
          </el-menu-item>
        </el-submenu>

        <!-- 系统配置 -->
        <el-submenu
          v-if="showGroupSystem"
          index="system"
        >
          <template slot="title">
            <i class="el-icon-s-tools" />
            <span>系统配置</span>
          </template>
          <el-menu-item
            v-if="hasPerm(PERMISSIONS.VIEW_CONFIG)"
            index="/admin/config"
          >
            <i class="el-icon-s-operation" />
            <span slot="title">全局配置</span>
          </el-menu-item>
          <el-menu-item
            v-if="hasPerm(PERMISSIONS.VIEW_ADMIN) && isSuperAdmin"
            index="/admin/permissions"
          >
            <i class="el-icon-lock" />
            <span slot="title">权限管理</span>
          </el-menu-item>
        </el-submenu>

        <!-- 审计中心 -->
        <el-submenu
          v-if="hasPerm(PERMISSIONS.VIEW_AUDIT)"
          index="audit"
        >
          <template slot="title">
            <i class="el-icon-document-copy" />
            <span>审计中心</span>
          </template>
          <el-menu-item index="/admin/audit">
            <i class="el-icon-document-checked" />
            <span slot="title">操作日志</span>
          </el-menu-item>
        </el-submenu>
      </el-menu>
    </el-aside>

    <el-container>
      <el-header class="admin-header" height="64px">
        <div class="header-left">
          <span class="header-title">{{ pageTitle }}</span>
        </div>
        <div class="header-right">
          <span
            v-if="userRole"
            class="user-role-tag"
          >
            {{ roleLabel }}
          </span>
          <el-divider direction="vertical" />
          <button
            class="header-btn"
            @click="goHome"
          >
            <svg class="w-4 h-4" fill="none" stroke="currentColor" stroke-width="2" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" d="M3 12l2-2m0 0l7-7 7 7M5 10v10a1 1 0 001 1h3m10-11l2 2m-2-2v10a1 1 0 01-1 1h-3m-6 0a1 1 0 001-1v-4a1 1 0 011-1h2a1 1 0 011 1v4a1 1 0 001 1m-6 0h6" />
            </svg>
            返回前台
          </button>
          <button
            class="header-btn logout"
            @click="logout"
          >
            <svg class="w-4 h-4" fill="none" stroke="currentColor" stroke-width="2" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1" />
            </svg>
            退出
          </button>
        </div>
      </el-header>

      <el-main class="admin-main">
        <transition name="page" mode="out-in">
          <router-view />
        </transition>
      </el-main>
    </el-container>
  </el-container>
</template>

<script>
import { mapState, mapGetters } from 'vuex'
import { ROLES, PERMISSIONS } from '../../data/adminConfig'

export default {
  name: 'AdminLayout',

  computed: {
    ...mapState(['userRole']),
    ...mapGetters(['hasPermission', 'isSuperAdmin']),

    PERMISSIONS() {
      return PERMISSIONS
    },

    showGroupContent() {
      return this.hasPerm(PERMISSIONS.VIEW_TOPIC) ||
        this.hasPerm(PERMISSIONS.VIEW_CASE) ||
        this.hasPerm(PERMISSIONS.VIEW_EXPERT)
    },

    showGroupUser() {
      return this.hasPerm(PERMISSIONS.VIEW_CONTRIBUTION)
    },

    showGroupSystem() {
      return this.hasPerm(PERMISSIONS.VIEW_CONFIG) ||
        (this.hasPerm(PERMISSIONS.VIEW_ADMIN) && this.isSuperAdmin)
    },

    pageTitle() {
      const titles = {
        '/admin': '数据看板',
        '/admin/experts': '专家管理',
        '/admin/topics': '话题管理',
        '/admin/cases': '案例管理',
        '/admin/scenarios': '场景题库',
        '/admin/diagnosis-templates': '诊断模板与配置',
        '/admin/matching-rules': '匹配规则',
        '/admin/users': '用户列表',
        '/admin/contributions': '投稿审核',
        '/admin/config': '全局配置',
        '/admin/permissions': '权限管理',
        '/admin/audit': '操作日志',
      }
      return titles[this.$route.path] || '管理后台'
    },

    roleLabel() {
      const labels = {
        [ROLES.USER]: '普通用户',
        [ROLES.ADMIN]: '普通管理员',
        [ROLES.SUPER_ADMIN]: '超级管理员',
      }
      return labels[this.userRole] || this.userRole
    },
  },

  created() {
    const normalizedRole = this.userRole === 'operator' ? ROLES.ADMIN : this.userRole
    if (normalizedRole === ROLES.USER) {
      this.$message.error('权限不足，无法访问管理后台')
      this.$router.replace('/')
    }
  },

  methods: {
    hasPerm(permission) {
      return this.hasPermission(permission)
    },

    goHome() {
      this.$router.push('/')
    },

    logout() {
      this.$store.commit('SET_IS_LOGGED_IN', false)
      this.$store.commit('SET_USER_ROLE', ROLES.USER)
      this.$store.commit('SET_CURRENT_ADMIN_ID', null)
      this.$router.push('/login')
    },
  },
}
</script>

<style scoped>
.admin-layout {
  height: 100vh;
  background: var(--color-bg);
}

/* ===== 侧边栏 ===== */
.admin-sidebar {
  background: #FFFFFF;
  display: flex;
  flex-direction: column;
  position: relative;
  border-right: 1px solid #E2E8F0;
  box-shadow: 0 0 20px rgba(0, 0, 0, 0.04);
}

/* 品牌区 */
.admin-brand {
  height: 64px;
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 0 20px;
  border-bottom: 1px solid #F1F5F9;
  position: relative;
  z-index: 1;
}

.brand-icon {
  width: 36px;
  height: 36px;
  background: linear-gradient(135deg, #F2C94C 0%, #D4A72E 100%);
  border-radius: 10px;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
}

.brand-text {
  display: flex;
  flex-direction: column;
  gap: 2px;
}

.brand-title {
  color: #0F172A;
  font-size: 15px;
  font-weight: 700;
  line-height: 1.2;
  letter-spacing: 0.5px;
}

.brand-subtitle {
  color: #94A3B8;
  font-size: 11px;
  display: flex;
  align-items: center;
  gap: 6px;
}

.brand-version {
  background: #F1F5F9;
  color: #64748B;
  padding: 1px 6px;
  border-radius: 4px;
  font-size: 10px;
}

/* 菜单 */
.admin-menu {
  border-right: none;
  background: transparent;
  flex: 1;
  padding: 8px 12px;
  position: relative;
  z-index: 1;
}

.admin-menu >>> .el-menu-item,
.admin-menu >>> .el-submenu__title {
  height: 44px;
  line-height: 44px;
  font-size: 13px;
  transition: all 0.25s ease;
  border-radius: 12px;
  margin-bottom: 2px;
}

.admin-menu >>> .el-menu-item:hover,
.admin-menu >>> .el-submenu__title:hover {
  background: #F8FAFC !important;
  color: #0F172A !important;
}

.admin-menu >>> .el-menu-item.is-active {
  background: linear-gradient(135deg, #F2C94C 0%, #D4A72E 100%) !important;
  color: #FFFFFF !important;
  border-right: none;
  box-shadow: 0 4px 12px rgba(242, 201, 76, 0.3);
}

.admin-menu >>> .el-submenu .el-menu-item {
  padding-left: 56px !important;
  min-width: auto;
  height: 40px;
  line-height: 40px;
  font-size: 13px;
}

.admin-menu >>> .el-submenu .el-menu-item.is-active {
  background: linear-gradient(135deg, #F2C94C 0%, #D4A72E 100%) !important;
  color: #FFFFFF !important;
  box-shadow: 0 4px 12px rgba(242, 201, 76, 0.3);
}

.admin-menu >>> .el-submenu.is-active .el-submenu__title {
  color: #D4A72E !important;
  font-weight: 600;
}

.admin-menu >>> .el-submenu.is-opened .el-submenu__title {
  background: #F8FAFC !important;
}

/* ===== 顶部 Header ===== */
.admin-header {
  background: rgba(255, 255, 255, 0.8);
  backdrop-filter: blur(12px);
  border-bottom: 1px solid #E2E8F0;
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 0 24px;
}

.header-left {
  display: flex;
  align-items: center;
  gap: 12px;
}

.header-title {
  font-size: 20px;
  font-weight: 800;
  color: #0F172A;
}

.header-right {
  display: flex;
  align-items: center;
  gap: 16px;
}

.user-role-tag {
  color: #B45309;
  font-size: 13px;
  font-weight: 600;
  background: linear-gradient(135deg, #FEF3C7 0%, #FDE68A 100%);
  padding: 4px 12px;
  border-radius: 9999px;
  border: 1px solid rgba(242, 201, 76, 0.3);
}

.header-btn {
  display: flex;
  align-items: center;
  gap: 6px;
  font-size: 13px;
  color: #64748B;
  background: none;
  border: none;
  cursor: pointer;
  padding: 6px 10px;
  border-radius: 8px;
  transition: all 0.2s ease;
}

.header-btn:hover {
  color: #D4A72E;
  background: rgba(242, 201, 76, 0.08);
}

.header-btn.logout:hover {
  color: #EF4444;
  background: rgba(239, 68, 68, 0.06);
}

/* ===== 主内容区 ===== */
.admin-main {
  padding: 24px;
  background: var(--color-bg);
  overflow-y: auto;
}

/* ===== 页面过渡动画 ===== */
.page-enter-active,
.page-leave-active {
  transition: opacity 0.35s ease, transform 0.35s ease;
}

.page-enter {
  opacity: 0;
  transform: translateY(16px);
}

.page-leave-to {
  opacity: 0;
  transform: translateY(-8px);
}
</style>
